import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";
import { useI18n } from "vue-i18n";

import expenseService, { type ExpenseCategory } from "@/services/expense";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type ExpenseFormState = {
  category_id: string;
  amount: string;
  description: string;
  warehouse_id: string;
};

type ExpenseCategoryForm = {
  code: string;
  name: string;
  description: string;
};

const createInitialForm = (): ExpenseFormState => ({
  category_id: "",
  amount: "",
  description: "",
  warehouse_id: "",
});

const createInitialCategoryForm = (): ExpenseCategoryForm => ({
  code: "",
  name: "",
  description: "",
});

const getPhnomPenhISOString = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Phnom_Penh",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);
  const getPart = (type: string) => parts.find((part) => part.type === type)?.value;

  return `${getPart("year")}-${getPart("month")}-${getPart("day")}T${getPart("hour")}:${getPart("minute")}`;
};

export function useExpenseForm() {
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();

  const referenceno = ref("");
  const date = ref(getPhnomPenhISOString(new Date()));
  const categories = ref<ExpenseCategory[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const isLoading = ref(false);
  const isCategorySaving = ref(false);

  const form = reactive<ExpenseFormState>(createInitialForm());
  const categoryForm = reactive<ExpenseCategoryForm>(createInitialCategoryForm());
  const expenseId = computed(() => Number(route.params.id || 0));
  const isEditMode = computed(() => Boolean(expenseId.value));
  const isCategoryEditMode = computed(() => false);

  const fetchNextRef = async () => {
    if (isEditMode.value) return;

    try {
      referenceno.value = await expenseService.getNextReference();
    } catch (error) {
      console.error("Error fetching next expense reference:", error);
    }
  };

  const fetchBaseData = async () => {
    const [categoryData, warehouseData] = await Promise.all([
      expenseService.getExpenseCategories(),
      warehouseService.getWarehouses(),
    ]);

    categories.value = categoryData;
    warehouses.value = warehouseData;
  };

  const generateCategoryCode = () => {
    const normalizedCodes = new Set(categories.value.map((item) => String(item.code || "").trim().toUpperCase()));
    let nextNumber = normalizedCodes.size + 1;

    while (normalizedCodes.has(`EXC-${String(nextNumber).padStart(3, "0")}`)) {
      nextNumber += 1;
    }

    categoryForm.code = `EXC-${String(nextNumber).padStart(3, "0")}`;
  };

  const openCategoryModal = () => {
    Object.assign(categoryForm, createInitialCategoryForm());
    generateCategoryCode();
  };

  const updateCategoryField = (field: keyof ExpenseCategoryForm, value: string) => {
    categoryForm[field] = value;
  };

  const hideCategoryModal = () => {
    const modalElement = document.getElementById("expenseCategoryModal");
    if (!modalElement) return;

    const BootstrapModal = Modal as any;
    const modalInstance =
      BootstrapModal.getInstance?.(modalElement) ||
      BootstrapModal.getOrCreateInstance?.(modalElement) ||
      new Modal(modalElement);

    modalInstance.hide();
  };

  const submitCategory = async () => {
    if (!categoryForm.code.trim() || !categoryForm.name.trim()) {
      await Swal.fire({
        icon: "warning",
        title: t("expense_category_page.missing_fields"),
        text: t("expense_category_page.code_and_name_required"),
      });
      return;
    }

    isCategorySaving.value = true;
    try {
      const createdCategory = await expenseService.createExpenseCategory({
        code: categoryForm.code.trim(),
        name: categoryForm.name.trim(),
        description: categoryForm.description.trim(),
      });

      await fetchBaseData();
      form.category_id = String(createdCategory.id ?? "");
      hideCategoryModal();

      await Swal.fire({
        icon: "success",
        title: t("expense_category_page.category_created"),
        text: t("expense_page.category_created_and_selected"),
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error: any) {
      console.error("Error creating expense category:", error);

      let message = t("expense_category_page.failed_to_save_expense_category");
      const apiMessage = error?.response?.data?.message;
      const validationErrors = error?.response?.data?.errors;

      if (apiMessage) {
        message = apiMessage;
      }

      if (validationErrors) {
        const lines = Object.values(validationErrors).flat().join("\n");
        if (lines) {
          message = `${message}\n${lines}`;
        }
      }

      await Swal.fire({
        icon: "error",
        title: t("expense_category_page.save_failed"),
        text: message,
      });
    } finally {
      isCategorySaving.value = false;
    }
  };

  const fetchExpenseDetails = async () => {
    if (!expenseId.value) return;

    isLoading.value = true;
    try {
      const expense = await expenseService.getExpense(expenseId.value);
      referenceno.value = expense.referenceno;

      if (expense.date) {
        date.value = getPhnomPenhISOString(new Date(expense.date));
      }

      form.category_id = expense.category_id ? String(expense.category_id) : "";
      form.amount = String(expense.amount ?? "");
      form.description = expense.description || "";
      form.warehouse_id = expense.warehouse_id ? String(expense.warehouse_id) : "";
    } catch (error) {
      console.error("Error fetching expense details:", error);
      await Swal.fire(t("expense_page.error"), t("expense_page.failed_to_fetch_expense_details"), "error");
      router.push("/expense-list");
    } finally {
      isLoading.value = false;
    }
  };

  const submitExpense = async () => {
    if (!referenceno.value.trim() || !form.category_id || !form.amount || !form.warehouse_id) {
      await Swal.fire(
        t("expense_page.error"),
        t("expense_page.required_fields_message"),
        "error"
      );
      return;
    }

    isLoading.value = true;
    try {
      const category = categories.value.find((item) => String(item.id) === form.category_id);

      const expenseData = {
        referenceno: referenceno.value.trim(),
        date: date.value,
        amount: Number(form.amount),
        name: category?.name || t("expenses"),
        category_id: Number(form.category_id),
        category_code: category?.code,
        category_name: category?.name,
        description: form.description,
        status: 1,
        warehouse_id: Number(form.warehouse_id),
      };

      if (isEditMode.value) {
        await expenseService.updateExpense(expenseId.value, expenseData);
        await Swal.fire(t("expense_page.success"), t("expense_page.expense_updated_successfully"), "success");
      } else {
        await expenseService.createExpense(expenseData);
        await Swal.fire(t("expense_page.success"), t("expense_page.expense_created_successfully"), "success");
      }

      router.push("/expense-list");
    } catch (error) {
      console.error("Error saving expense:", error);
      await Swal.fire(t("expense_page.error"), t("expense_page.failed_to_save_expense"), "error");
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(async () => {
    await fetchBaseData();

    if (isEditMode.value) {
      await fetchExpenseDetails();
    } else {
      await fetchNextRef();
    }
  });

  return {
    referenceno,
    date,
    categories,
    warehouses,
    form,
    categoryForm,
    isLoading,
    isCategorySaving,
    isEditMode,
    isCategoryEditMode,
    fetchNextRef,
    openCategoryModal,
    updateCategoryField,
    generateCategoryCode,
    submitCategory,
    submitExpense,
  };
}
